import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import type { WorkspaceDomain } from 'honeygrid-types'
import { Globe, MoreHorizontal, Plus } from 'lucide-react'

interface DomainsTableProps {
	domains: WorkspaceDomain[]
	onSelect: (domain: WorkspaceDomain) => void
	onAdd: () => void
	selectedId?: string
}

const getStatusVariant = (status: string): 'default' | 'secondary' | 'destructive' => {
	switch (status) {
		case 'verified':
			return 'default'
		case 'pending':
			return 'secondary'
		case 'failed':
		case 'expired':
			return 'destructive'
		default:
			return 'secondary'
	}
}

export function DomainsTable({ domains, onSelect, onAdd, selectedId }: DomainsTableProps) {
	return (
		<Table aria-label="Domains">
			<TableHeader className="border-t border-t-[#3A2E2F]/20 border-b border-b-[#3A2E2F] [&_tr]:!border-0">
				<TableRow>
					<TableHead>Domain</TableHead>
					<TableHead>Type</TableHead>
					<TableHead>Status</TableHead>
					<TableHead className="w-12"></TableHead>
				</TableRow>
			</TableHeader>
			<TableBody className="[&_tr:last-child]:!border-b [&_tr]:!border-0">
				{domains.length === 0 ? (
					<TableRow className="!border-b !border-b-[#3A2E2F]/20">
						<TableCell colSpan={4} className="h-32 p-0">
							<div className="flex flex-col items-center justify-center text-center space-y-3 bg-[#3A2E2F]/5 h-full py-8">
								<Globe className="h-8 w-8 text-foreground/60" />
								<p className="text-sm font-medium text-muted-foreground">No domains yet</p>
								<Button variant="outline" size="sm" onClick={onAdd}>
									<Plus className="h-4 w-4 mr-2" />
									Add Domain
								</Button>
							</div>
						</TableCell>
					</TableRow>
				) : (
					domains.map((domain) => (
						<TableRow
							key={domain.uuid}
							className={`group cursor-pointer hover:bg-muted/50 !border-b !border-b-[#3A2E2F]/20 ${
								domain.uuid === selectedId ? 'bg-muted' : ''
							}`}
							onClick={() => onSelect(domain)}
						>
							<TableCell className="font-medium">
								{domain.type === 'subdomain'
									? `${domain.pattern}.tryhoneygrid.com`
									: domain.pattern}
								{domain.is_primary && ' ⭐'}
							</TableCell>
							<TableCell>
								<Badge variant="secondary" className="capitalize">
									{domain.type}
								</Badge>
							</TableCell>
							<TableCell>
								<Badge variant={getStatusVariant(domain.verification_status)}>
									{domain.verification_status}
								</Badge>
							</TableCell>
							<TableCell>
								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<Button
											variant="ghost"
											size="sm"
											className="h-8 w-8 p-0"
											aria-label={`More actions for ${domain.pattern}`}
										>
											<MoreHorizontal className="h-4 w-4" />
										</Button>
									</DropdownMenuTrigger>
									<DropdownMenuContent align="end">
										<DropdownMenuItem
											onClick={(e) => {
												e.stopPropagation()
												onSelect(domain)
											}}
										>
											Edit
										</DropdownMenuItem>
										<DropdownMenuItem
											onClick={(e) => {
												e.stopPropagation()
												console.log('Delete domain:', domain.uuid)
											}}
										>
											Delete
										</DropdownMenuItem>
									</DropdownMenuContent>
								</DropdownMenu>
							</TableCell>
						</TableRow>
					))
				)}
			</TableBody>
		</Table>
	)
}